import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { InputWithLabel, TextInput, RadioGroupHorizontal } from 'wix-rich-content-ui-components';
import { containsUrl, startsWithHttps, hasProtocol } from 'wix-rich-content-common';
import { identity, trimStart } from 'lodash';
import { SRC_TYPE_HTML, SRC_TYPE_URL } from '../defaults.js';
import AdsenseTitle from '../AdsenseTitle.js';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';

var css_248z = ".OQ19S,.VsdBT,.glob_fontElementMap{font-family:var(--ricos-font-family,unset)}[dir] .CoyVW{padding:18px 0 15px}[dir] .sN29S{margin:18px 0 13px}.cI6C9{height:146px}.LzQRt{display:flex;justify-content:space-between}.VsdBT{font-size:14px;font-weight:300;height:21px;line-height:1.5}[dir] .VsdBT{background:transparent;border:0;cursor:pointer;padding:0}.EhetT{color:var(--ricos-settings-action-color,var(--ricos-action-color-fallback,#0261ff))}._8xKw4{color:var(--ricos-settings-text-color,#333)}";
var styles = {"htmlEditPanel_button":"VsdBT","fontElementMap":"OQ19S","htmlEditPanel":"CoyVW","htmlEditPanel_input":"sN29S","htmlEditPanel_textArea":"cI6C9","htmlEditPanel_buttons":"LzQRt","htmlEditPanel_primaryButton":"EhetT","htmlEditPanel_secondaryButton":"_8xKw4"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var _a;
const VALIDATORS = {
  [SRC_TYPE_HTML]: () => null,
  [SRC_TYPE_URL]: (url) => {
    let error = null;
    if (!url || !containsUrl(url)) {
      error = "HtmlEditPanel_UrlError";
    } else if (!startsWithHttps(url) && hasProtocol(url)) {
      error = "HtmlEditPanel_HttpsError";
    }
    return error;
  }
};
const NORMALIZERS = {
  [SRC_TYPE_HTML]: identity,
  [SRC_TYPE_URL]: (url) => hasProtocol(url) ? url : `https://${trimStart(url, "//")}`
};
class HtmlEditPanel extends Component {
  constructor() {
    super(...arguments);
    __publicField(this, "initialData", this.props.componentData);
    __publicField(this, "state", {
      srcType: ((_a = this.initialData.config) == null ? void 0 : _a.isAdsense) ? "html" : this.initialData.srcType,
      [this.initialData.srcType]: this.initialData.src,
      submitted: false
    });
    __publicField(this, "handleSrcTypeChange", (srcType) => {
      this.setState({ srcType });
    });
    __publicField(this, "handleSrcChange", (target) => {
      const { name, value } = target;
      this.setState({ [name]: value }, () => this.updateComponentData());
    });
    __publicField(this, "handleCancelClick", () => {
      this.props.updateData(this.initialData);
      this.props.close();
    });
    __publicField(this, "updateComponentData", () => {
      const { srcType } = this.state;
      if (this.isValid()) {
        setTimeout(
          () => this.props.updateData({
            srcType,
            src: NORMALIZERS[srcType](this.state[srcType]) || ""
          }),
          0
        );
      }
      this.setState({ submitted: true });
    });
    __publicField(this, "getError", () => {
      const { srcType } = this.state;
      return VALIDATORS[srcType](this.state[srcType]);
    });
    __publicField(this, "isValid", () => !this.getError());
    __publicField(this, "render", () => {
      const { srcType, submitted } = this.state;
      const {
        t,
        tabIndex,
        componentData: { config }
      } = this.props;
      const inputBaseProps = {
        onChange: this.handleSrcChange,
        getTarget: true,
        tabIndex
      };
      const textInput = srcType === SRC_TYPE_HTML || (config == null ? void 0 : config.isAdsense) ? /* @__PURE__ */ React.createElement("div", { className: styles.htmlEditPanel_textArea }, /* @__PURE__ */ React.createElement(
        InputWithLabel,
        __spreadValues({
          name: SRC_TYPE_HTML,
          value: this.state[SRC_TYPE_HTML],
          placeholder: (config == null ? void 0 : config.isAdsense) ? t("HtmlEditPanel_HtmlInput_AdSense_Placeholder") : t("HtmlEditPanel_HtmlInput_Placeholder"),
          isTextArea: true,
          isFullHeight: true,
          dataHook: "htmlEditPanel_htmlInput"
        }, inputBaseProps)
      )) : /* @__PURE__ */ React.createElement(
        TextInput,
        __spreadValues({
          name: SRC_TYPE_URL,
          value: this.state[SRC_TYPE_URL],
          "data-hook": "htmlEditPanel_htmlInput",
          error: submitted ? t(this.getError()) : null,
          placeholder: t("HtmlEditPanel_UrlInput_Placeholder")
        }, inputBaseProps)
      );
      return /* @__PURE__ */ React.createElement("div", { className: styles.htmlEditPanel }, (config == null ? void 0 : config.isAdsense) ? /* @__PURE__ */ React.createElement(AdsenseTitle, { t }) : /* @__PURE__ */ React.createElement(
        RadioGroupHorizontal,
        {
          name: "srcType",
          value: this.state.srcType,
          onChange: this.handleSrcTypeChange,
          dataSource: [
            {
              value: SRC_TYPE_HTML,
              labelText: t("HtmlEditPanel_Code"),
              dataHook: "htmlEditPanel_radioHtml"
            },
            {
              value: SRC_TYPE_URL,
              labelText: t("HtmlEditPanel_Source"),
              dataHook: "htmlEditPanel_radioUrl"
            }
          ],
          inline: true
        }
      ), /* @__PURE__ */ React.createElement("div", { className: styles.htmlEditPanel_input }, textInput), /* @__PURE__ */ React.createElement("div", { className: styles.htmlEditPanel_buttons }, /* @__PURE__ */ React.createElement(
        "button",
        {
          className: classNames(
            styles.htmlEditPanel_button,
            styles.htmlEditPanel_secondaryButton
          ),
          onClick: this.handleCancelClick,
          "data-hook": "htmlEditPanel_Cancel",
          type: "button"
        },
        t("HtmlEditPanel_Cancel")
      ), /* @__PURE__ */ React.createElement(
        "button",
        {
          className: classNames(styles.htmlEditPanel_button, styles.htmlEditPanel_primaryButton),
          onClick: this.props.close,
          "data-hook": "htmlEditPanel_Update",
          type: "button"
        },
        t("HtmlEditPanel_Update")
      )));
    });
  }
}
HtmlEditPanel.propTypes = {
  componentData: PropTypes.shape({
    srcType: PropTypes.string.isRequired,
    src: PropTypes.any,
    config: PropTypes.object
  }).isRequired,
  t: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  tabIndex: PropTypes.number,
  updateData: PropTypes.func
};

export { HtmlEditPanel as default };
//# sourceMappingURL=HtmlEditPanel.js.map
